import React from 'react';
import { Shield, Lock, Eye, FileText, Users, Database } from 'lucide-react';

export const PrivacyPolicyPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="mx-auto h-16 w-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
              <Shield className="h-8 w-8 text-blue-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Privacy Policy</h1>
            <p className="text-gray-600">Castlereagh Hire Safety Induction Portal</p>
            <p className="text-sm text-gray-500 mt-2">Last updated: {new Date().toLocaleDateString()}</p>
          </div>

          {/* Introduction */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <FileText className="h-6 w-6 mr-2 text-blue-600" />
              Introduction
            </h2>
            <p className="text-gray-700 leading-relaxed">
              Castlereagh Hire ("we," "our," or "us") is committed to protecting your privacy and personal information. 
              This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our 
              Safety Induction Portal (the "Service"). This policy applies to all users of our safety management system, 
              including workers, supervisors, and administrative personnel.
            </p>
          </section>

          {/* Information We Collect */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <Database className="h-6 w-6 mr-2 text-green-600" />
              Information We Collect
            </h2>
            
            <div className="space-y-6">
              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center">
                  <Users className="h-5 w-5 mr-2 text-green-600" />
                  Personal Information
                </h3>
                <ul className="list-disc list-inside text-gray-700 space-y-2">
                  <li><strong>Identity Information:</strong> Full name, email address, phone number</li>
                  <li><strong>Employment Details:</strong> Job role, start date, employment status</li>
                  <li><strong>Certification Data:</strong> Safety certifications, white card numbers, qualification details</li>
                  <li><strong>Digital Signatures:</strong> Electronic signatures for safety induction completion</li>
                  <li><strong>Photographs:</strong> Worker identification photos (when provided)</li>
                </ul>
              </div>

              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-3">Safety and Compliance Information</h3>
                <ul className="list-disc list-inside text-gray-700 space-y-2">
                  <li><strong>Induction Records:</strong> Safety training completion status, dates, and acknowledgments</li>
                  <li><strong>Site Assignment Data:</strong> Work site assignments and access permissions</li>
                  <li><strong>Compliance History:</strong> Safety compliance records and audit trails</li>
                  <li><strong>Incident Reports:</strong> Safety-related incidents or observations (if applicable)</li>
                </ul>
              </div>

              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-3">Technical Information</h3>
                <ul className="list-disc list-inside text-gray-700 space-y-2">
                  <li><strong>Usage Data:</strong> Login times, system access patterns, feature usage</li>
                  <li><strong>Device Information:</strong> Browser type, operating system, IP address</li>
                  <li><strong>Session Data:</strong> Authentication tokens and session management information</li>
                </ul>
              </div>
            </div>
          </section>

          {/* How We Use Information */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <Eye className="h-6 w-6 mr-2 text-orange-600" />
              How We Use Your Information
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-orange-50 rounded-lg p-6 border border-orange-200">
                <h3 className="text-lg font-medium text-orange-900 mb-3">Safety Management</h3>
                <ul className="list-disc list-inside text-orange-800 space-y-2 text-sm">
                  <li>Process and track safety inductions</li>
                  <li>Verify worker qualifications and certifications</li>
                  <li>Manage site access and permissions</li>
                  <li>Generate safety compliance reports</li>
                  <li>Maintain audit trails for regulatory compliance</li>
                </ul>
              </div>

              <div className="bg-blue-50 rounded-lg p-6 border border-blue-200">
                <h3 className="text-lg font-medium text-blue-900 mb-3">Operational Purposes</h3>
                <ul className="list-disc list-inside text-blue-800 space-y-2 text-sm">
                  <li>Authenticate and authorize system access</li>
                  <li>Communicate safety updates and requirements</li>
                  <li>Provide technical support and assistance</li>
                  <li>Improve system functionality and user experience</li>
                  <li>Ensure system security and prevent unauthorized access</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Information Sharing */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Information Sharing and Disclosure</h2>
            
            <div className="space-y-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h3 className="text-lg font-medium text-yellow-900 mb-2">We may share your information with:</h3>
                <ul className="list-disc list-inside text-yellow-800 space-y-1 text-sm">
                  <li><strong>Authorized Personnel:</strong> Site supervisors, safety officers, and management staff</li>
                  <li><strong>Regulatory Authorities:</strong> Government agencies for compliance and safety inspections</li>
                  <li><strong>Emergency Services:</strong> In case of workplace incidents or emergencies</li>
                  <li><strong>Legal Requirements:</strong> When required by law, court order, or legal process</li>
                  <li><strong>Service Providers:</strong> Third-party vendors who assist in system operation (under strict confidentiality agreements)</li>
                </ul>
              </div>

              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <h3 className="text-lg font-medium text-red-900 mb-2">We will NOT:</h3>
                <ul className="list-disc list-inside text-red-800 space-y-1 text-sm">
                  <li>Sell your personal information to third parties</li>
                  <li>Use your information for marketing purposes without consent</li>
                  <li>Share information with unauthorized parties</li>
                  <li>Disclose information beyond what is necessary for safety and compliance</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Data Security */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <Lock className="h-6 w-6 mr-2 text-red-600" />
              Data Security
            </h2>
            
            <div className="bg-gray-50 rounded-lg p-6">
              <p className="text-gray-700 mb-4">
                We implement industry-standard security measures to protect your personal information:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <ul className="list-disc list-inside text-gray-700 space-y-2 text-sm">
                  <li><strong>Encryption:</strong> Data encrypted in transit and at rest</li>
                  <li><strong>Access Controls:</strong> Role-based access with authentication</li>
                  <li><strong>Regular Backups:</strong> Secure data backup and recovery procedures</li>
                  <li><strong>Monitoring:</strong> Continuous system monitoring and threat detection</li>
                </ul>
                <ul className="list-disc list-inside text-gray-700 space-y-2 text-sm">
                  <li><strong>Secure Infrastructure:</strong> Cloud-based security with Supabase</li>
                  <li><strong>Regular Updates:</strong> Security patches and system updates</li>
                  <li><strong>Staff Training:</strong> Regular security awareness training</li>
                  <li><strong>Incident Response:</strong> Procedures for security breach response</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Data Retention */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Data Retention</h2>
            <div className="bg-blue-50 rounded-lg p-6 border border-blue-200">
              <p className="text-blue-800 mb-4">
                We retain your personal information for as long as necessary to fulfill the purposes outlined in this policy:
              </p>
              <ul className="list-disc list-inside text-blue-800 space-y-2 text-sm">
                <li><strong>Active Employment:</strong> Throughout your employment or engagement with Castle Reagh Hire</li>
                <li><strong>Safety Records:</strong> Minimum 7 years as required by Australian workplace safety regulations</li>
                <li><strong>Compliance Requirements:</strong> As mandated by relevant industry standards and legal obligations</li>
                <li><strong>Archived Data:</strong> Securely archived for historical compliance and audit purposes</li>
              </ul>
            </div>
          </section>

          {/* Your Rights */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Your Privacy Rights</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                  <h3 className="text-lg font-medium text-green-900 mb-2">Access Rights</h3>
                  <ul className="list-disc list-inside text-green-800 space-y-1 text-sm">
                    <li>Request access to your personal information</li>
                    <li>Obtain copies of your safety records</li>
                    <li>Review your induction and compliance history</li>
                  </ul>
                </div>

                <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                  <h3 className="text-lg font-medium text-blue-900 mb-2">Correction Rights</h3>
                  <ul className="list-disc list-inside text-blue-800 space-y-1 text-sm">
                    <li>Request correction of inaccurate information</li>
                    <li>Update your contact details and certifications</li>
                    <li>Amend incomplete or outdated records</li>
                  </ul>
                </div>
              </div>

              <div className="space-y-4">
                <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                  <h3 className="text-lg font-medium text-yellow-900 mb-2">Limitation Rights</h3>
                  <ul className="list-disc list-inside text-yellow-800 space-y-1 text-sm">
                    <li>Request restriction of processing (where legally permissible)</li>
                    <li>Object to certain uses of your information</li>
                    <li>Withdraw consent for non-essential processing</li>
                  </ul>
                </div>

                <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                  <h3 className="text-lg font-medium text-red-900 mb-2">Important Note</h3>
                  <p className="text-red-800 text-sm">
                    Some information cannot be deleted or modified due to legal and safety compliance requirements 
                    under Australian workplace health and safety legislation.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Cookies and Tracking */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Cookies and Tracking Technologies</h2>
            <div className="bg-gray-50 rounded-lg p-6">
              <p className="text-gray-700 mb-4">
                Our system uses essential cookies and similar technologies to:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li><strong>Authentication:</strong> Maintain secure login sessions</li>
                <li><strong>Functionality:</strong> Remember user preferences and settings</li>
                <li><strong>Security:</strong> Detect and prevent unauthorized access</li>
                <li><strong>Performance:</strong> Monitor system performance and reliability</li>
              </ul>
              <p className="text-gray-700 mt-4 text-sm">
                We do not use tracking cookies for advertising or marketing purposes. All cookies are essential 
                for system operation and safety compliance.
              </p>
            </div>
          </section>

          {/* Third-Party Services */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Third-Party Services</h2>
            <div className="bg-blue-50 rounded-lg p-6 border border-blue-200">
              <p className="text-blue-800 mb-4">
                Our system utilizes the following third-party services under strict data protection agreements:
              </p>
              <ul className="list-disc list-inside text-blue-800 space-y-2 text-sm">
                <li><strong>Supabase:</strong> Database and authentication services (data stored in Australia/Singapore)</li>
                <li><strong>Netlify:</strong> Web hosting and content delivery</li>
                <li><strong>Cloud Storage:</strong> Secure document and image storage</li>
              </ul>
              <p className="text-blue-800 mt-4 text-sm">
                All third-party providers are required to maintain equivalent privacy and security standards.
              </p>
            </div>
          </section>

          {/* Contact Information */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Contact Us</h2>
            <div className="bg-orange-50 rounded-lg p-6 border border-orange-200">
              <p className="text-orange-800 mb-4">
                If you have questions about this Privacy Policy or wish to exercise your privacy rights, please contact us:
              </p>
              <div className="space-y-2 text-orange-800">
                <p><strong>Castlereagh Hire</strong></p>
                <p><strong>Email:</strong> privacy@castlereaghhire.com.au</p>
                <p><strong>Phone:</strong> +61 2 9999 9999</p>
                <p><strong>Address:</strong> Sydney, NSW, Australia</p>
              </div>
              <p className="text-orange-800 mt-4 text-sm">
                We will respond to your privacy inquiries within 30 days of receipt.
              </p>
            </div>
          </section>

          {/* Changes to Policy */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Changes to This Privacy Policy</h2>
            <div className="bg-gray-50 rounded-lg p-6">
              <p className="text-gray-700">
                We may update this Privacy Policy from time to time to reflect changes in our practices, 
                technology, legal requirements, or other factors. We will notify users of any material 
                changes by posting the updated policy on our system and updating the "Last updated" date. 
                Continued use of the Safety Induction Portal after such changes constitutes acceptance of 
                the updated Privacy Policy.
              </p>
            </div>
          </section>

          {/* Compliance */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Regulatory Compliance</h2>
            <div className="bg-green-50 rounded-lg p-6 border border-green-200">
              <p className="text-green-800 mb-4">
                This Privacy Policy and our data practices comply with:
              </p>
              <ul className="list-disc list-inside text-green-800 space-y-2 text-sm">
                <li><strong>Privacy Act 1988 (Cth)</strong> - Australian Privacy Principles</li>
                <li><strong>Work Health and Safety Act 2011</strong> - Workplace safety record requirements</li>
                <li><strong>Fair Work Act 2009</strong> - Employment record obligations</li>
                <li><strong>Australian Information Security Manual (ISM)</strong> - Government security guidelines</li>
              </ul>
            </div>
          </section>

          {/* Footer */}
          <div className="text-center pt-8 border-t border-gray-200">
            <p className="text-sm text-gray-500">
              This Privacy Policy is effective as of {new Date().toLocaleDateString()} and applies to all users of the 
              Castlereagh Hire Safety Induction Portal.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};